import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

export type ToastStatus = 'success' | 'error' | 'info';

export interface ToastInput {
  status?: ToastStatus;
  title: string;
  description?: string;
  durationMs?: number;
}

interface ToastRecord extends ToastInput {
  id: number;
  leaving: boolean;
}

interface ToastContextValue {
  toast: (input: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const counterRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    const existing = timersRef.current.get(id);
    if (existing) {
      clearTimeout(existing);
      timersRef.current.delete(id);
    }
    const removeTimer = setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
    }, 220);
    timersRef.current.set(id, removeTimer);
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      counterRef.current += 1;
      const id = counterRef.current;
      const record: ToastRecord = {
        id,
        status: input.status ?? 'info',
        title: input.title,
        description: input.description,
        durationMs: input.durationMs ?? 4200,
        leaving: false,
      };
      setToasts((current) => [...current, record]);
      const duration = record.durationMs ?? 4200;
      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  return (
    <ToastContext.Provider value={value}>
      {children}
      {portalTarget
        ? createPortal(
            <div className="toast-container" role="region" aria-label="Notifications">
              {toasts.map((t) => (
                <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
              ))}
            </div>,
            portalTarget,
          )
        : null}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: number) => void }) {
  const statusIcon = (() => {
    switch (toast.status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4" aria-hidden />;
      case 'error':
        return <AlertCircle className="h-4 w-4" aria-hidden />;
      default:
        return <Info className="h-4 w-4" aria-hidden />;
    }
  })();

  return (
    <div
      className={`toast toast--${toast.status ?? 'info'} ${toast.leaving ? 'toast--leaving' : ''}`.trim()}
      role="status"
    >
      <span className="toast__icon">{statusIcon}</span>
      <div className="toast__body">
        <p className="toast__title">{toast.title}</p>
        {toast.description ? <p className="toast__description">{toast.description}</p> : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="toast__close"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside a <ToastProvider>');
  }
  return ctx;
}
